import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { posteingangApi, vertraegeApi, kategorienApi } from '../api';

// ─── Konstanten ────────────────────────────────────────────────────────────────

const TERMINAL_STATES = ['ERKANNT', 'ZU_PRUEFEN', 'ZUGEORDNET', 'FEHLER', 'DUPLIKAT', 'IGNORIERT'];

const KLASSE_CONFIG = {
  HIGH:   { label: 'Sehr wahrscheinlich', color: '#34d399', bg: '#052e16', icon: '✅' },
  MEDIUM: { label: 'Möglicherweise',      color: '#fbbf24', bg: '#422006', icon: '⚠️' },
  LOW:    { label: 'Schwacher Treffer',   color: '#64748b', bg: '#1e293b', icon: '🔍' },
};

const FIELD_KLASSE_CONFIG = {
  MISSING_IN_CONTRACT: { label: 'Neu', color: '#34d399', bg: '#052e16' },
  DIFFERENT:           { label: 'Abweichend', color: '#f87171', bg: '#431407' },
  IDENTICAL:           { label: 'Identisch', color: '#64748b', bg: '#1e293b' },
};

// ─── Stile ────────────────────────────────────────────────────────────────────

const S = {
  page: { padding: '24px', maxWidth: '820px', margin: '0 auto' },
  card: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' },
  h1:   { margin: 0, fontSize: '22px', fontWeight: 700, color: '#f1f5f9' },
  h2:   { margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#f1f5f9' },
  stepBadge: (n) => ({
    background: '#818cf8', color: 'white', borderRadius: '50%', width: 24, height: 24,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    marginRight: 10, fontSize: 13, flexShrink: 0,
  }),
  inp: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #334155', color: '#f1f5f9', background: '#0f172a', fontSize: '14px', boxSizing: 'border-box' },
  lbl: { display: 'block', marginBottom: '6px', fontWeight: 600, color: '#94a3b8', fontSize: '13px' },
  err: { background: '#431407', color: '#fca5a5', border: '1px solid #ef4444', padding: '12px', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' },
  info: { background: '#1e1b4b', color: '#c7d2fe', border: '1px solid #818cf8', padding: '12px', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' },
};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

async function pollStatus(intakeId, signal) {
  for (let i = 0; i < 60; i++) {
    if (signal.aborted) throw new Error('Abgebrochen');
    const entry = await posteingangApi.get(intakeId);
    if (TERMINAL_STATES.includes(entry.intake_status)) return entry;
    await sleep(2000);
  }
  throw new Error('Zeitlimit überschritten – Analyse läuft noch');
}

function MatchBadge({ konfidenz }) {
  const cfg = KLASSE_CONFIG[konfidenz] || KLASSE_CONFIG.LOW;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function FieldRow({ feld, checked, onToggle, conflictChoice, onConflictChoice }) {
  const cfg = FIELD_KLASSE_CONFIG[feld.classification] || {};
  const isMissing = feld.classification === 'MISSING_IN_CONTRACT';
  const isDiff    = feld.classification === 'DIFFERENT';
  const isIdentical = feld.classification === 'IDENTICAL';

  return (
    <div style={{ borderBottom: '1px solid #334155', padding: '12px 0', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      {(isMissing || isDiff) && (
        <input type="checkbox" checked={!!checked} onChange={e => onToggle(feld.feldname, e.target.checked)}
          style={{ marginTop: 3, accentColor: '#818cf8', flexShrink: 0 }} />
      )}
      {isIdentical && <span style={{ color: '#22c55e', fontSize: 18, flexShrink: 0 }}>✓</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#f1f5f9' }}>{feld.label}</span>
          <span style={{ background: cfg.bg, color: cfg.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
            {cfg.label}
          </span>
        </div>
        {isMissing && (
          <div style={{ background: '#052e16', borderRadius: 6, padding: '8px 10px', border: '1px solid #22c55e44' }}>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Neuer Wert</div>
            <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 600 }}>{feld.document_value || '–'}</div>
          </div>
        )}
        {isDiff && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px', border: '1px solid #334155' }}>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Bisher</div>
              <div style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'line-through' }}>{feld.contract_value || '–'}</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px', border: '1px solid #818cf844' }}>
              <div style={{ fontSize: 10, color: '#818cf8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>Neu</div>
              <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 600 }}>{feld.document_value || '–'}</div>
            </div>
          </div>
        )}
        {isIdentical && (
          <div style={{ fontSize: 13, color: '#64748b' }}>{feld.contract_value}</div>
        )}
      </div>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function VertragScanPage() {
  const navigate = useNavigate();
  const fileRef   = useRef();
  const cameraRef = useRef();
  const abortRef  = useRef(new AbortController());

  const [step, setStep]         = useState('UPLOAD');   // UPLOAD | PROCESSING | RESULT | ENRICH | NEW_FORM | DONE
  const [datei, setDatei]       = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [fehler, setFehler]     = useState('');

  const [intakeEntry, setIntakeEntry] = useState(null);
  const [vertraege, setVertraege]     = useState([]);
  const [kategorien, setKategorien]   = useState([]);
  const [selectedVertrag, setSelectedVertrag] = useState('');

  const [feldvergleich, setFeldvergleich]   = useState([]);
  const [checkedFelder, setCheckedFelder]   = useState({});

  const [newForm, setNewForm] = useState(null);
  const [doneVertragId, setDoneVertragId] = useState(null);

  useEffect(() => {
    kategorienApi.list().then(r => setKategorien(Array.isArray(r) ? r : [])).catch(() => {});
    return () => abortRef.current.abort();
  }, []);

  // ─── Datei wählen ───────────────────────────────────────────────────────────

  function onDateiWaehlen(e) {
    const f = e.target.files[0];
    if (!f) return;
    setDatei(f);
    setFehler('');
    setVorschau(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  }

  // ─── Analysieren ────────────────────────────────────────────────────────────

  async function analysieren() {
    if (!datei) return;
    setLoading(true);
    setFehler('');
    setStep('PROCESSING');

    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      const formData = new FormData();
      formData.append('file', datei);
      const uploadResult = await posteingangApi.upload(formData);
      const intakeId = uploadResult.id;

      const entry = await pollStatus(intakeId, signal);
      setIntakeEntry(entry);

      if (entry.intake_status === 'DUPLIKAT') {
        setStep('DUPLIKAT');
        return;
      }
      if (entry.intake_status === 'FEHLER') {
        setFehler(entry.fehler || 'Analyse fehlgeschlagen');
        setStep('UPLOAD');
        return;
      }

      // Verträge für manuellen Wähler laden
      const vRes = await vertraegeApi.list();
      const vData = vRes?.data ?? vRes;
      setVertraege(Array.isArray(vData) ? vData : (vData?.vertraege || []));

      setStep('RESULT');

      // Wenn HIGH-Match: direkt Feldvergleich laden
      if (entry.bester_match_konfidenz === 'HIGH' && entry.bester_match_vertrag_id) {
        await ladeFelder(intakeId, entry.bester_match_vertrag_id);
        setSelectedVertrag(String(entry.bester_match_vertrag_id));
      }

      // Vorausfüllen für neuen Vertrag
      const extFelder = entry.extrahierte_felder || [];
      const fm = {};
      for (const f of extFelder) fm[f.feldname] = f.normalisierter_wert ?? f.erkannter_wert;
      // Kreditgeber is the actual Vertragspartner; kreditvermittler must NOT become anbieter
      const anbieter = fm.kreditgeber || fm.anbieter || '';
      setNewForm({
        titel:                  anbieter || entry.klassifikation || '',
        anbieter:               anbieter,
        kategorie_id:           '',
        kosten:                 '', // Never auto-fill from credit rate (Tilgung+Zins ≠ laufende Kosten)
        zahlungsintervall:      fm.zahlungsintervall || 'monatlich',
        vertragsbeginn:         fm.vertragsbeginn || '',
        vertragsende:           fm.vertragsende || '',
        kuendigungsfrist_monate: fm.kuendigungsfrist_monate || '1',
        auto_renewal:           true,
        notizen:                '',
        vertragsnummer:         fm.antragsnummer || fm.vertragsnummer || '',
        kundennummer:           fm.kundennummer || '',
        // Credit info fields — displayed read-only, not sent as contract fields
        _kreditgeber:           fm.kreditgeber || '',
        _kreditvermittler:      fm.kreditvermittler || '',
        _antragsnummer:         fm.antragsnummer || '',
        _nettodarlehensbetrag:  fm.nettodarlehensbetrag || '',
        _monatliche_rate:       fm.monatliche_rate || '',
        _laufzeit_monate:       fm.laufzeit_monate || '',
        _sollzinssatz:          fm.sollzinssatz || '',
        _effektiver_jahreszins: fm.effektiver_jahreszins || '',
        _gesamtbetrag_kredit:   fm.gesamtbetrag_kredit || '',
      });

    } catch (e) {
      if (!signal.aborted) {
        setFehler(e.response?.data?.error || e.message || 'Fehler bei der Analyse');
        setStep('UPLOAD');
      }
    } finally {
      setLoading(false);
    }
  }

  // ─── Feldvergleich laden ────────────────────────────────────────────────────

  const ladeFelder = useCallback(async (intakeId, vertragId) => {
    try {
      const res = await posteingangApi.feldvergleich(intakeId, vertragId);
      const felder = res.felder || [];
      setFeldvergleich(felder);
      const initial = {};
      for (const f of felder) {
        if (f.classification === 'MISSING_IN_CONTRACT') initial[f.feldname] = true;
        if (f.classification === 'DIFFERENT')           initial[f.feldname] = false;
      }
      setCheckedFelder(initial);
    } catch {
      setFeldvergleich([]);
    }
  }, []);

  async function onVertragWaehlen(vertragId) {
    setSelectedVertrag(String(vertragId));
    if (intakeEntry && vertragId) {
      await ladeFelder(intakeEntry.id, vertragId);
    }
    setStep('ENRICH');
  }

  // ─── Bestehenden Vertrag verwenden ─────────────────────────────────────────

  async function bestehendenVerwenden() {
    if (!selectedVertrag || !intakeEntry) return;
    setLoading(true);
    setFehler('');
    try {
      const uebernehmen = feldvergleich
        .filter(f => f.classification === 'MISSING_IN_CONTRACT' && checkedFelder[f.feldname])
        .map(f => ({ feldname: f.feldname, wert: f.document_value }));
      const aendern = feldvergleich
        .filter(f => f.classification === 'DIFFERENT' && checkedFelder[f.feldname])
        .map(f => ({ feldname: f.feldname, wert: f.document_value }));

      await posteingangApi.zuordnen(intakeEntry.id, parseInt(selectedVertrag), uebernehmen, aendern);
      setDoneVertragId(parseInt(selectedVertrag));
      setStep('DONE');
    } catch (e) {
      setFehler(e.response?.data?.error || e.message || 'Fehler beim Zuordnen');
    } finally {
      setLoading(false);
    }
  }

  // ─── Neuen Vertrag anlegen ─────────────────────────────────────────────────

  async function neuenVertragAnlegen(e) {
    e.preventDefault();
    if (!intakeEntry) return;
    setLoading(true);
    setFehler('');
    try {
      const res = await posteingangApi.neuerVertrag(intakeEntry.id, {
        ...newForm,
        kosten: newForm.kosten !== '' ? parseFloat(newForm.kosten) : null,
        kuendigungsfrist_monate: parseInt(newForm.kuendigungsfrist_monate) || 1,
        auto_renewal: newForm.auto_renewal ? 1 : 0,
      });
      setDoneVertragId(res.vertrag_id);
      setStep('DONE');
    } catch (e) {
      setFehler(e.response?.data?.error || e.message || 'Fehler beim Anlegen');
    } finally {
      setLoading(false);
    }
  }

  // ─── Renderfunktionen ──────────────────────────────────────────────────────

  if (step === 'DONE') {
    return (
      <div style={{ ...S.page, textAlign: 'center', paddingTop: 60 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#10b981', marginBottom: 8 }}>Erfolgreich gespeichert!</h2>
        <p style={{ color: '#64748b', marginBottom: 24 }}>Dokument ist in der Vertragsakte gespeichert.</p>
        <button className="btn-primary" onClick={() => navigate(`/vertraege/${doneVertragId}`)}>
          Zur Vertragsakte →
        </button>
      </div>
    );
  }

  if (step === 'DUPLIKAT' && intakeEntry) {
    const dupRef   = intakeEntry.duplikat_referenz;
    const dupDok   = intakeEntry.duplikat_dokument_ref;
    const dokId    = dupDok?.id || dupRef?.dokument_id || null;
    const vertragId = dupDok?.vertrag_id || dupRef?.zugeordnet_vertrag_id || null;
    const vertragTitel = dupDok?.vertrag_titel
      ? `${dupDok.vertrag_anbieter ? dupDok.vertrag_anbieter + ' – ' : ''}${dupDok.vertrag_titel}`
      : null;
    const dateiname = dupDok?.originaldateiname || dupRef?.originaldateiname || intakeEntry.originaldateiname;
    const letzteAnalyse = dupRef?.erstellt_am || intakeEntry.verarbeitet_am;

    async function startReanalyse() {
      try {
        setLoading(true);
        await posteingangApi.reanalysieren(intakeEntry.id);
        setStep('PROCESSING');
        abortRef.current = new AbortController();
        const entry = await pollStatus(intakeEntry.id, abortRef.current.signal);
        setIntakeEntry(entry);
        if (entry.intake_status === 'FEHLER') {
          setFehler(entry.fehler || 'Re-Analyse fehlgeschlagen');
          setStep('UPLOAD');
          return;
        }
        const vRes = await vertraegeApi.list();
        const vData = vRes?.data ?? vRes;
        setVertraege(Array.isArray(vData) ? vData : (vData?.vertraege || []));
        if (entry.bester_match_konfidenz === 'HIGH' && entry.bester_match_vertrag_id) {
          await ladeFelder(entry.id, entry.bester_match_vertrag_id);
          setSelectedVertrag(String(entry.bester_match_vertrag_id));
        }
        const extFelder = entry.extrahierte_felder || [];
        const fm = {};
        for (const f of extFelder) fm[f.feldname] = f.normalisierter_wert ?? f.erkannter_wert;
        const anbieterR = fm.kreditgeber || fm.anbieter || '';
        setNewForm({
          titel: anbieterR || entry.klassifikation || '', anbieter: anbieterR, kategorie_id: '',
          kosten: '', zahlungsintervall: fm.zahlungsintervall || 'monatlich',
          vertragsbeginn: fm.vertragsbeginn || '', vertragsende: fm.vertragsende || '',
          kuendigungsfrist_monate: fm.kuendigungsfrist_monate || '1', auto_renewal: true,
          notizen: '', vertragsnummer: fm.antragsnummer || fm.vertragsnummer || '', kundennummer: fm.kundennummer || '',
          _kreditgeber: fm.kreditgeber || '', _kreditvermittler: fm.kreditvermittler || '',
          _antragsnummer: fm.antragsnummer || '', _nettodarlehensbetrag: fm.nettodarlehensbetrag || '',
          _monatliche_rate: fm.monatliche_rate || '', _laufzeit_monate: fm.laufzeit_monate || '',
          _sollzinssatz: fm.sollzinssatz || '', _effektiver_jahreszins: fm.effektiver_jahreszins || '',
          _gesamtbetrag_kredit: fm.gesamtbetrag_kredit || '',
        });
        setStep('RESULT');
      } catch (e) {
        setFehler(e.response?.data?.error || e.message || 'Re-Analyse fehlgeschlagen');
      } finally {
        setLoading(false);
      }
    }

    return (
      <div style={S.page}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate('/vertraege')} className="btn-ghost" style={{ fontSize: 20 }}>←</button>
          <h1 style={S.h1}>🔁 Dokument bereits vorhanden</h1>
        </div>
        {fehler && <div style={S.err}>{fehler}</div>}
        <div style={S.card}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 18 }}>
            <span style={{ fontSize: 36 }}>📄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                Dieses Dokument ist bereits gespeichert
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Kein zweites Dokument wird angelegt.</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {dateiname && (
              <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                <span style={{ color: '#94a3b8', minWidth: 110 }}>Datei</span>
                <span style={{ fontWeight: 600, color: '#1e293b', wordBreak: 'break-all' }}>{dateiname}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
              <span style={{ color: '#94a3b8', minWidth: 110 }}>Zugeordneter Vertrag</span>
              <span style={{ fontWeight: 600, color: vertragId ? '#059669' : '#94a3b8' }}>
                {vertragTitel || (vertragId ? `Vertrag #${vertragId}` : 'Noch nicht zugeordnet')}
              </span>
            </div>
            {letzteAnalyse && (
              <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                <span style={{ color: '#94a3b8', minWidth: 110 }}>Letzte Analyse</span>
                <span style={{ color: '#475569' }}>{new Date(letzteAnalyse).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            )}
          </div>

          <div style={{ fontSize: 13, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
            Du kannst das Dokument erneut analysieren, um weitere Vertragsfelder zu erkennen und den Vertrag zu ergänzen – ohne das Dokument doppelt zu speichern.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn-primary" style={{ width: '100%' }} disabled={loading} onClick={startReanalyse}>
              {loading ? 'Wird analysiert…' : '🔍 Erneut analysieren'}
            </button>
            {dokId && (
              <button className="btn-secondary" style={{ width: '100%' }}
                onClick={() => navigate(`/dokumente/${dokId}`)}>
                📄 Vorhandenes Dokument öffnen
              </button>
            )}
            {vertragId && (
              <button className="btn-secondary" style={{ width: '100%' }}
                onClick={() => navigate(`/vertraege/${vertragId}`)}>
                📋 Vertrag öffnen
              </button>
            )}
            {!vertragId && dokId && (
              <button className="btn-secondary" style={{ width: '100%' }}
                onClick={() => navigate(`/dokumente/${dokId}`)}>
                📌 Dokument einem Vertrag zuordnen
              </button>
            )}
            <button className="btn-secondary" style={{ width: '100%' }}
              onClick={() => { setStep('UPLOAD'); setDatei(null); setIntakeEntry(null); setFehler(''); }}>
              Zurück
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/vertraege')} className="btn-ghost" style={{ fontSize: 20 }}>←</button>
        <h1 style={S.h1}>🤖 Vertrag scannen & zuordnen</h1>
      </div>

      {fehler && <div style={S.err}>{fehler}</div>}

      {/* ─── SCHRITT 1: Upload ──────────────────────────────────────────────────── */}
      {(step === 'UPLOAD' || step === 'PROCESSING') && (
        <div style={S.card}>
          <h2 style={S.h2}>
            <span style={S.stepBadge(1)}>1</span>
            Dokument hochladen
          </h2>

          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onDateiWaehlen} />
          <input ref={fileRef}   type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={onDateiWaehlen} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <button onClick={() => cameraRef.current?.click()} disabled={loading}
              style={{ padding: 20, border: '2px dashed #6366f1', borderRadius: 12, background: '#eef2ff', cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
              <div style={{ fontWeight: 600, color: '#6366f1' }}>Kamera</div>
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={loading}
              style={{ padding: 20, border: '2px dashed #e2e8f0', borderRadius: 12, background: '#f8fafc', cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              <div style={{ fontWeight: 600, color: '#374151' }}>Datei wählen</div>
            </button>
          </div>

          {datei && (
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 24 }}>{datei.type.startsWith('image/') ? '🖼️' : '📄'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{datei.name}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>{(datei.size / 1024).toFixed(0)} KB</div>
              </div>
              {vorschau && <img src={vorschau} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />}
            </div>
          )}

          {step === 'PROCESSING' ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#6366f1', fontWeight: 600 }}>
              <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}>⚙️</div>
              Dokument wird analysiert und Verträge geprüft…
              <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 8, fontWeight: 400 }}>Dies kann bis zu 30 Sekunden dauern.</div>
            </div>
          ) : (
            <button onClick={analysieren} disabled={!datei || loading} style={{
              width: '100%', padding: 14, background: !datei ? '#94a3b8' : '#818cf8',
              color: 'white', border: 'none', borderRadius: 10, cursor: !datei ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 15,
            }}>
              🚀 Analysieren & Verträge prüfen
            </button>
          )}
        </div>
      )}

      {/* ─── SCHRITT 2: Ergebnis & Entscheidung ────────────────────────────────── */}
      {step === 'RESULT' && intakeEntry && (
        <div>
          {/* Analyse-Info */}
          <div style={{ ...S.card, background: '#f8fafc', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 28 }}>📄</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{intakeEntry.originaldateiname}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {intakeEntry.klassifikation || 'Dokument'} •{' '}
                {intakeEntry.content_length ? `${intakeEntry.content_length} Zeichen erkannt` : 'Text erkannt'}
              </div>
            </div>
            {intakeEntry.extrahierte_felder?.length > 0 && (
              <div style={{ textAlign: 'right', fontSize: 13, color: '#64748b' }}>
                <strong style={{ color: '#6366f1' }}>{intakeEntry.extrahierte_felder.length}</strong> Felder erkannt
              </div>
            )}
          </div>

          {/* HIGH MATCH */}
          {intakeEntry.bester_match_konfidenz === 'HIGH' && intakeEntry.bester_match_vertrag_id && (() => {
            const mv = vertraege.find(v => v.id === intakeEntry.bester_match_vertrag_id);
            return (
              <div style={{ ...S.card, border: '2px solid #10b981' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 22 }}>✅</span>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, color: '#059669', fontSize: 16 }}>Bestehender Vertrag erkannt</h2>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Sehr hohe Übereinstimmung – kein neuer Vertrag nötig</div>
                  </div>
                  <MatchBadge konfidenz="HIGH" />
                </div>
                <div style={{ background: '#ecfdf5', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{intakeEntry.bester_match_vertrag_titel}</div>
                  {mv && [
                    mv.vertragsnummer && ['Vertragsnummer', mv.vertragsnummer],
                    mv.kundennummer   && ['Kundennummer',   mv.kundennummer],
                    mv.kosten != null && ['Kosten', `${Number(mv.kosten).toFixed(2)} € / ${mv.zahlungsintervall || 'Monat'}`],
                    mv.vertragsbeginn && ['Beginn',         mv.vertragsbeginn],
                    mv.vertragsende   && ['Ende',           mv.vertragsende],
                  ].filter(Boolean).map(([lbl, val]) => (
                    <div key={lbl} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#047857', marginBottom: 2 }}>
                      <span style={{ minWidth: 110, opacity: 0.7 }}>{lbl}</span>
                      <span style={{ fontWeight: 600 }}>{val}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                    Übereinstimmung: {intakeEntry.bester_match_score}% • {intakeEntry.bester_match_begruendung}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ flex: 1 }}
                    onClick={() => { setStep('ENRICH'); setSelectedVertrag(String(intakeEntry.bester_match_vertrag_id)); }}>
                    ✅ Bestehenden Vertrag ergänzen
                  </button>
                  <button className="btn-secondary" onClick={() => setStep('NEW_FORM')}>
                    Neuen Vertrag anlegen
                  </button>
                </div>
              </div>
            );
          })()}

          {/* MEDIUM / LOW MATCH */}
          {(intakeEntry.bester_match_konfidenz === 'MEDIUM' || intakeEntry.bester_match_konfidenz === 'LOW') &&
            intakeEntry.bester_match_vertrag_id && (() => {
            const mv = vertraege.find(v => v.id === intakeEntry.bester_match_vertrag_id);
            const score = intakeEntry.bester_match_score || 0;
            const isWeak = score < 40;
            const signale = (intakeEntry.bester_match_begruendung || '')
              .split(',').map(s => s.trim()).filter(Boolean);
            const extFelder = {};
            (intakeEntry.extrahierte_felder || []).forEach(f => {
              extFelder[f.feldname] = f.normalisierter_wert ?? f.erkannter_wert;
            });
            const na = v => (v != null && v !== '') ? String(v) : null;
            const fmtKosten = (k, i) => k != null ? `${Number(k).toFixed(2)} € / ${i || 'Monat'}` : null;
            const borderColor = isWeak ? '#ef4444' : '#f59e0b';
            return (
              <div style={{ ...S.card, border: `2px solid ${borderColor}` }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 22 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, color: isWeak ? '#dc2626' : '#d97706', fontSize: 16 }}>
                      Möglicherweise bereits vorhanden
                    </h2>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {isWeak
                        ? 'Schwacher Treffer – bitte sorgfältig prüfen'
                        : 'Bitte prüfe, ob dieser Vertrag passt'}
                    </div>
                  </div>
                  <MatchBadge konfidenz={intakeEntry.bester_match_konfidenz} />
                </div>

                {/* Übereinstimmungs-Balken */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    <span>Übereinstimmung</span>
                    <span style={{ fontWeight: 700, color: isWeak ? '#dc2626' : '#d97706' }}>{score}%</span>
                  </div>
                  <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(score, 100)}%`, background: borderColor, borderRadius: 3 }} />
                  </div>
                </div>

                {/* Match-Signale */}
                {signale.length > 0 && (
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Erkannte Übereinstimmungen
                    </div>
                    {signale.map((s, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>✓ {s}</div>
                    ))}
                  </div>
                )}

                {/* Vergleich Vertrag vs. Dokument */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  {/* Bestehender Vertrag */}
                  <div style={{ flex: 1, minWidth: 0, background: '#f8fafc', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Bestehender Vertrag
                    </div>
                    {[
                      ['Anbieter',       na(mv?.anbieter)],
                      ['Vertragsnr.',    na(mv?.vertragsnummer)],
                      ['Kundennr.',      na(mv?.kundennummer)],
                      ['Kosten',         fmtKosten(mv?.kosten, mv?.zahlungsintervall)],
                      ['Beginn',         na(mv?.vertragsbeginn)],
                      ['Ende',           na(mv?.vertragsende)],
                    ].map(([lbl, val]) => (
                      <div key={lbl} style={{ marginBottom: 5 }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{lbl}</div>
                        <div style={{ fontSize: 12, color: val ? '#1e293b' : '#cbd5e1', fontStyle: val ? 'normal' : 'italic', wordBreak: 'break-all' }}>
                          {val ?? 'Nicht hinterlegt'}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Neues Dokument */}
                  <div style={{ flex: 1, minWidth: 0, background: '#fffbeb', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Neues Dokument
                    </div>
                    {[
                      ['Anbieter',       na(extFelder.anbieter)],
                      ['Vertragsnr.',    na(extFelder.vertragsnummer)],
                      ['Kundennr.',      na(extFelder.kundennummer)],
                      ['Kosten',         extFelder.kosten_monatlich != null
                          ? `${Number(extFelder.kosten_monatlich).toFixed(2)} € / Monat` : null],
                      ['Beginn',         na(extFelder.vertragsbeginn)],
                      ['Ende',           na(extFelder.vertragsende)],
                    ].map(([lbl, val]) => (
                      <div key={lbl} style={{ marginBottom: 5 }}>
                        <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600, opacity: 0.7 }}>{lbl}</div>
                        <div style={{ fontSize: 12, color: val ? '#1e293b' : '#cbd5e1', fontStyle: val ? 'normal' : 'italic', wordBreak: 'break-all' }}>
                          {val ?? 'Nicht erkannt'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ flex: 1 }}
                    onClick={() => onVertragWaehlen(intakeEntry.bester_match_vertrag_id)}>
                    Diesen Vertrag verwenden →
                  </button>
                  <button className="btn-secondary"
                    onClick={() => setStep('NEW_FORM')}>
                    Neuen Vertrag anlegen
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Alle Vorschläge als Alternative + manuell wählen */}
          <div style={S.card}>
            <h2 style={S.h2}>
              <span style={S.stepBadge(2)}>2</span>
              {intakeEntry.bester_match_vertrag_id ? 'Anderen Vertrag manuell wählen' : 'Vertrag wählen oder neu anlegen'}
            </h2>

            {!intakeEntry.bester_match_vertrag_id && (
              <div style={S.info}>Kein bestehender Vertrag erkannt. Du kannst das Dokument einem bestehenden Vertrag zuordnen oder einen neuen anlegen.</div>
            )}

            <div>
              <label style={S.lbl}>Bestehenden Vertrag wählen</label>
              <select style={S.inp} value={selectedVertrag}
                onChange={e => setSelectedVertrag(e.target.value)}>
                <option value="">-- Vertrag wählen --</option>
                {vertraege.map(v => (
                  <option key={v.id} value={v.id}>{v.anbieter ? `${v.anbieter} – ` : ''}{v.titel}</option>
                ))}
              </select>
            </div>

            {selectedVertrag && (
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button className="btn-primary" style={{ flex: 1 }}
                  onClick={() => onVertragWaehlen(parseInt(selectedVertrag))}>
                  Vertrag prüfen & zuordnen →
                </button>
              </div>
            )}

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #334155' }}>
              <button className="btn-primary" style={{ width: '100%' }} onClick={() => setStep('NEW_FORM')}>
                ➕ Neuen Vertrag aus diesem Dokument anlegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SCHRITT 3a: Enrichment ────────────────────────────────────────────── */}
      {step === 'ENRICH' && intakeEntry && (
        <div>
          <div style={S.card}>
            <h2 style={S.h2}>
              <span style={S.stepBadge(3)}>3</span>
              Felder prüfen & übernehmen
            </h2>

            {(() => {
              const missing = feldvergleich.filter(f => f.classification === 'MISSING_IN_CONTRACT');
              const diff    = feldvergleich.filter(f => f.classification === 'DIFFERENT');
              const identical = feldvergleich.filter(f => f.classification === 'IDENTICAL');

              return (
                <>
                  {feldvergleich.length === 0 && (
                    <div style={S.info}>Keine Felder aus dem Dokument extrahiert oder noch ausstehend.</div>
                  )}

                  {missing.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#059669', marginBottom: 8 }}>
                        ➕ Neue Informationen ({missing.length})
                      </div>
                      {missing.map(f => (
                        <FieldRow key={f.feldname} feld={f}
                          checked={!!checkedFelder[f.feldname]}
                          onToggle={(n, v) => setCheckedFelder(p => ({ ...p, [n]: v }))} />
                      ))}
                    </div>
                  )}

                  {diff.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#dc2626', marginBottom: 8 }}>
                        ⚡ Mögliche Änderungen ({diff.length}) – Bitte prüfen
                      </div>
                      {diff.map(f => (
                        <FieldRow key={f.feldname} feld={f}
                          checked={!!checkedFelder[f.feldname]}
                          onToggle={(n, v) => setCheckedFelder(p => ({ ...p, [n]: v }))} />
                      ))}
                    </div>
                  )}

                  {identical.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                        ✓ Identische Felder ({identical.length})
                      </div>
                      {identical.map(f => (
                        <FieldRow key={f.feldname} feld={f}
                          checked={false} onToggle={() => {}} />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-secondary" onClick={() => setStep('RESULT')}>← Zurück</button>
            <button className="btn-primary" style={{ flex: 1 }} disabled={loading} onClick={bestehendenVerwenden}>
              {loading ? '💾 Speichern…' : '✅ Dokument zuordnen & Felder übernehmen'}
            </button>
          </div>
        </div>
      )}

      {/* ─── SCHRITT 3b: Neuer Vertrag ─────────────────────────────────────────── */}
      {step === 'NEW_FORM' && newForm && (
        <div style={S.card}>
          <h2 style={S.h2}>
            <span style={S.stepBadge(3)}>3</span>
            Neuen Vertrag anlegen
          </h2>

          {/* Credit info — read-only, shown when credit fields present */}
          {(newForm._nettodarlehensbetrag || newForm._monatliche_rate || newForm._kreditgeber) && (
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px', marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Erkannte Kreditinformationen
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
                {[
                  ['Kreditgeber',       newForm._kreditgeber],
                  ['Kreditvermittler',  newForm._kreditvermittler],
                  ['Antragsnummer',     newForm._antragsnummer],
                  ['Nettodarlehen',     newForm._nettodarlehensbetrag ? `${Number(newForm._nettodarlehensbetrag).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : ''],
                  ['Monatliche Rate',   newForm._monatliche_rate ? `${Number(newForm._monatliche_rate).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : ''],
                  ['Laufzeit',         newForm._laufzeit_monate ? `${newForm._laufzeit_monate} Monate` : ''],
                  ['Sollzinssatz',     newForm._sollzinssatz ? `${newForm._sollzinssatz} %` : ''],
                  ['Effektiver Zins',  newForm._effektiver_jahreszins ? `${newForm._effektiver_jahreszins} %` : ''],
                  ['Gesamtbetrag',     newForm._gesamtbetrag_kredit ? `${Number(newForm._gesamtbetrag_kredit).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : ''],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label}>
                    <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600 }}>{label}</div>
                    <div style={{ color: '#f1f5f9', fontWeight: 600 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: '#475569' }}>
                Die Kreditrate wird nicht als laufende Kosten übernommen (Tilgung + Zins). Kreditfelder bleiben im Dokument gespeichert.
              </div>
            </div>
          )}

          <form onSubmit={neuenVertragAnlegen} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={S.lbl}>Titel *</label>
                <input style={S.inp} required value={newForm.titel}
                  onChange={e => setNewForm(p => ({ ...p, titel: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Anbieter</label>
                <input style={S.inp} value={newForm.anbieter}
                  onChange={e => setNewForm(p => ({ ...p, anbieter: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={S.lbl}>Kosten (€)</label>
                <input style={S.inp} type="number" step="0.01" value={newForm.kosten}
                  onChange={e => setNewForm(p => ({ ...p, kosten: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Intervall</label>
                <select style={S.inp} value={newForm.zahlungsintervall}
                  onChange={e => setNewForm(p => ({ ...p, zahlungsintervall: e.target.value }))}>
                  <option value="monatlich">Monatlich</option>
                  <option value="vierteljaehrlich">Vierteljährlich</option>
                  <option value="halbjaehrlich">Halbjährlich</option>
                  <option value="jaehrlich">Jährlich</option>
                </select>
              </div>
            </div>
            <div>
              <label style={S.lbl}>Kategorie</label>
              <select style={S.inp} value={newForm.kategorie_id}
                onChange={e => setNewForm(p => ({ ...p, kategorie_id: e.target.value }))}>
                <option value="">-- Kategorie wählen --</option>
                {kategorien.map(k => <option key={k.id} value={k.id}>{k.icon} {k.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={S.lbl}>Beginn</label>
                <input style={S.inp} type="date" value={newForm.vertragsbeginn}
                  onChange={e => setNewForm(p => ({ ...p, vertragsbeginn: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Ende</label>
                <input style={S.inp} type="date" value={newForm.vertragsende}
                  onChange={e => setNewForm(p => ({ ...p, vertragsende: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Kündigungsfrist (Monate)</label>
                <input style={S.inp} type="number" min="0" value={newForm.kuendigungsfrist_monate}
                  onChange={e => setNewForm(p => ({ ...p, kuendigungsfrist_monate: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={S.lbl}>Vertragsnummer</label>
                <input style={S.inp} value={newForm.vertragsnummer}
                  onChange={e => setNewForm(p => ({ ...p, vertragsnummer: e.target.value }))} />
              </div>
              <div>
                <label style={S.lbl}>Kundennummer</label>
                <input style={S.inp} value={newForm.kundennummer}
                  onChange={e => setNewForm(p => ({ ...p, kundennummer: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn-secondary" onClick={() => setStep('RESULT')}>← Zurück</button>
              <button type="submit" disabled={loading} className="btn-primary" style={{ flex: 1 }}>
                {loading ? '💾 Speichern…' : '✅ Vertrag anlegen & Dokument speichern'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
